// ---------------------------------------------------------------
// Copyright (c) Aspen Publishing. All rights reserved.
// ---------------------------------------------------------------

class CartLogin extends HTMLElement {
  constructor() {
    super();

    this.popup = this.closest('popup-component');
    this.steps = this.querySelectorAll('.popup-wrapper');
    this.loginButtons = this.querySelectorAll('.jsBtn-navigate[data-class="login-layout"]');
    this.registerButtons = this.querySelectorAll('.jsBtn-navigate[data-class="register-layout"]');
    this.backButton = this.querySelector('.popup-back.jsBack');

    this._currentStep = parseInt(this.dataset.currentStep || 1);

    this.init();
  }

  init() {
    this.addEventListeners();
    this.showCurrentStep();
  }

  get currentStep() {
    return this._currentStep;
  }

  set currentStep(value) {
    this._currentStep = value;
    this.dataset.currentStep = value.toString();
    this.showCurrentStep();
  }

  addEventListeners() {
    this.loginButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.currentStep = 1;
      });
    });

    this.registerButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.currentStep = 2;
      });
    });

    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        this.currentStep = 1;
      });
    }
  }

  showCurrentStep() {
    this.steps.forEach((step, index) => {
      if (index + 1 === this.currentStep) {
        step.classList.remove('hide');
      } else {
        step.classList.add('hide');
      }
    });

    // Mostrar u ocultar el botón back según el paso actual
    if (this.backButton) {
      if (this.currentStep === 2) {
        this.backButton.style.display = '';
      } else {
        this.backButton.style.display = 'none';
      }
    }
  }

  open() {
    if (this.popup) {
      this.popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('popup-active');
    }
  }

  close() {
    if (this.popup) {
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('popup-active');
    }
  }
}

if (!customElements.get('cart-login')) {
  customElements.define('cart-login', CartLogin);
}

function isUserAuthenticated() {
  try {
    const cartComponent = document.querySelector('cart-component');

    if (cartComponent && cartComponent.dataset) {
      const isLoggedIn = cartComponent.dataset.customerLogin === 'true';

      return isLoggedIn;
    }
  } catch {
    // Ignorar errores de selección de DOM
  }

  if (window.customer && window.customer.id) {
    return true;
  }

  return false;
}

function hasOnlyLeafProduct() {
  const cartItems = document.querySelectorAll('cart-item');

  if (cartItems && cartItems.length) {
    return Array.from(cartItems).some(item => {
      const platforms = (item.dataset.platforms || '').split(',');

      return platforms.length === 1 && platforms.includes('leaf');
    });
  }

  return false;
}

let _hasSellingPlanInCartCache = null;
let _cartCheckInProgress = false;

async function checkSellingPlanInCart() {
  if (_cartCheckInProgress) {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        if (!_cartCheckInProgress && _hasSellingPlanInCartCache !== null) {
          clearInterval(checkInterval);
          resolve(_hasSellingPlanInCartCache);
        }
      }, 100);
    });
  }

  if (_hasSellingPlanInCartCache !== null) {
    return Promise.resolve(_hasSellingPlanInCartCache);
  }

  _cartCheckInProgress = true;

  try {
    const response = await fetch('/cart.js');

    if (!response.ok) {
      throw new Error(`Error al obtener el carrito: ${response.status}`);
    }

    const cart = await response.json();

    if (!cart.items || !Array.isArray(cart.items)) {
      return false;
    }

    const hasSelllingPlan = cart.items.some(item => item.selling_plan_allocation != null);

    _hasSellingPlanInCartCache = hasSelllingPlan;

    window.cartData = cart;

    return hasSelllingPlan;
  } catch (error) {
    console.error('Error al verificar selling plans:', error);

    return false;
  } finally {
    _cartCheckInProgress = false;
  }
}

// Función utilizada para actualizar el caché y evitar múltiples llamadas API
// eslint-disable-next-line no-unused-vars
function hasSellingPlanInCart() {
  if (_hasSellingPlanInCartCache !== null) {
    return _hasSellingPlanInCartCache;
  }

  if (!_cartCheckInProgress) {
    checkSellingPlanInCart().then(result => {
      _hasSellingPlanInCartCache = result;
    });
  }

  return false;
}

function removeExistingPopup() {
  const existingPopup = document.querySelector('#cart-login-popup');

  if (existingPopup) {
    document.body.classList.remove('popup-active');
    existingPopup.remove();

    return true;
  }

  return false;
}

function closePopup(popup) {
  document.body.classList.remove('popup-active');

  if (popup) {
    popup.setAttribute('aria-hidden', 'true');
    popup.style.opacity = '0';
    popup.style.visibility = 'hidden';

    setTimeout(() => {
      removeExistingPopup();
    }, 300);
  }
}

async function showCartLoginPopup() {
  removeExistingPopup();

  try {
    document.body.classList.add('loading');

    const response = await fetch('/cart?section_id=popup-cart-login');

    if (!response.ok) {
      throw new Error(`Error al cargar el popup: ${response.status}`);
    }

    const html = await response.text();

    const parts = html.split('<!-- split -->');

    if (parts.length < 2 || !parts[1]) {
      throw new Error('Formato de respuesta incorrecto');
    }

    const popupContent = parts[1];

    document.body.insertAdjacentHTML('beforeend', popupContent);

    const popup = document.querySelector('#cart-login-popup');

    if (!popup) {
      throw new Error('No se pudo encontrar el popup después de insertarlo');
    }

    const popupLayer = popup.querySelector('.popup-layer');
    const popupContentElement = popup.querySelector('.popup__content');

    const closeButtons = popup.querySelectorAll('.jsClose');

    closeButtons.forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        closePopup(popup);
      });
    });

    if (popupLayer && popupContentElement) {
      popupLayer.style.opacity = '1';

      setTimeout(() => {
        popupContentElement.style.opacity = '1';
        popupContentElement.style.transform = 'translateY(0)';
      }, 50);
    }

    document.body.classList.add('popup-active');
    popup.setAttribute('aria-hidden', 'false');

    document.addEventListener('click', function popupOutsideClick(event) {
      if (
        popup &&
        !event.target.closest('.popup__content') &&
        !event.target.closest('.jsToggle-popup') &&
        !event.target.closest('button[type="submit"]') &&
        !event.target.closest('input')
      ) {
        closePopup(popup);
        document.removeEventListener('click', popupOutsideClick);
      }
    });
  } catch {
    document.body.classList.remove('loading');
  } finally {
    document.body.classList.remove('loading');
  }

  return false;
}

const GA4_BEGIN_CHECKOUT_TIMEOUT = 1500;

function buildGa4Items(cart) {
  return (cart.items || []).map((item, index) => ({
    item_id: item.sku || String(item.variant_id),
    item_name: item.product_title || item.title,
    item_variant: item.variant_title || undefined,
    item_brand: item.vendor || undefined,
    item_category: item.product_type || undefined,
    price: (item.final_price ?? item.price ?? 0) / 100,
    quantity: item.quantity,
    index,
  }));
}

async function pushBeginCheckout() {
  if (window.__beginCheckoutFired) {
    return;
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GA4_BEGIN_CHECKOUT_TIMEOUT);

  let cart;

  try {
    cart = await fetch(`${root}cart.js`, { signal: controller.signal }).then(response => response.json());
  } finally {
    clearTimeout(timer);
  }

  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
    return;
  }

  const coupon = (cart.cart_level_discount_applications || [])[0]?.title;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });

  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: cart.currency,
      value: (cart.total_price || 0) / 100,
      coupon,
      items: buildGa4Items(cart),
    },
  });

  window.__beginCheckoutFired = true;
}

async function proceedToCheckout() {
  try {
    await pushBeginCheckout();
  } catch (error) {
    console.warn('[begin_checkout] Could not push the event before checkout:', error);
  }

  try {
    window.location.href = '/checkout';
  } catch (error) {
    console.error('Could not navigate to checkout:', error);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const setupCheckoutListeners = () => {
    const checkoutButtons = document.querySelectorAll(
      '#checkout, .checkout-button, .btn-cart-checkout, [href="/checkout"], button[name="checkout"], [name="checkout"], [action="/checkout"]'
    );

    checkoutButtons.forEach(button => {
      if (button.tagName.toLowerCase() !== 'form') {
        const clonedButton = button.cloneNode(true);

        if (button.parentNode) {
          button.parentNode.replaceChild(clonedButton, button);
        }

        clonedButton.addEventListener('click', async function (event) {
          event.preventDefault();

          const isAuthenticated = isUserAuthenticated();

          if (!isAuthenticated) {
            try {
              const hasSelllingPlan = await checkSellingPlanInCart();
              const hasLeaf = hasOnlyLeafProduct();

              if (hasSelllingPlan || hasLeaf) {
                event.stopPropagation();
                showCartLoginPopup();

                return false;
              }
            } catch (error) {
              console.error('Error al verificar selling plans:', error);
            }
          }

          await proceedToCheckout();

          return false;
        });
      }
    });

    const checkoutForms = document.querySelectorAll('form[action="/checkout"], form#cart');

    checkoutForms.forEach(form => {
      const clonedForm = form.cloneNode(true);

      if (form.parentNode) {
        form.parentNode.replaceChild(clonedForm, form);
      }

      clonedForm.addEventListener('submit', async function (event) {
        event.preventDefault();

        const isAuthenticated = isUserAuthenticated();

        if (!isAuthenticated) {
          try {
            const hasSelllingPlan = await checkSellingPlanInCart();

            if (hasSelllingPlan) {
              event.stopPropagation();
              showCartLoginPopup();

              return false;
            }
          } catch (error) {
            console.error('Error al verificar selling plans:', error);
          }
        }

        await proceedToCheckout();

        return false;
      });
    });
  };

  const setupMutationObserver = () => {
    const cartContainer = document.querySelector('cart-items, .cart-container, .cart, #CartDrawer');

    if (cartContainer) {
      const observer = new MutationObserver(() => {
        setupCheckoutListeners();
      });

      observer.observe(cartContainer, { childList: true, subtree: true });
    }
  };

  setupCheckoutListeners();
  setupMutationObserver();
});
